import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetUserStartTimeLazyQuery,
  useSetUserStartTimeMutation,
  useGetUserDurationMinutesLazyQuery,
} from "@/graphql/generated";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";

type UsePatientTimerOptions = {
  userId?: string | null;
  isPatient: boolean;
};

export function usePatientTimer({ userId, isPatient }: UsePatientTimerOptions) {
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [isMeetOver, setIsMeetOver] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  const { patientLine, roomData } = useSelector(selectCurrentRoom);
  const [getStartTime] = useGetUserStartTimeLazyQuery();
  const [setStartTime] = useSetUserStartTimeMutation();
  const [getDurationMinutes] = useGetUserDurationMinutesLazyQuery();

  const patient = useMemo(() => {
    if (!userId || !patientLine) return null;
    return patientLine.find((p) => p.id === userId);
  }, [userId, patientLine]);

  // Try to get duration_minutes from Redux first, otherwise fetch it
  const [fetchedDurationMinutes, setFetchedDurationMinutes] = useState<
    number | null
  >(null);
  const durationMinutes = useMemo(() => {
    // First try Redux (available for doctors/admins)
    if (
      patient?.duration_minutes !== undefined &&
      patient.duration_minutes !== null
    ) {
      return patient.duration_minutes;
    }
    // Otherwise use fetched value (for patients)
    return fetchedDurationMinutes;
  }, [patient?.duration_minutes, fetchedDurationMinutes]);

  const durationSeconds = useMemo(() => {
    if (!durationMinutes || durationMinutes === 0) return null;
    return durationMinutes * 60;
  }, [durationMinutes]);

  // Fetch duration_minutes if not available in Redux
  useEffect(() => {
    if (!userId || !isPatient) return;
    if (
      patient?.duration_minutes !== undefined &&
      patient.duration_minutes !== null
    )
      return; // Already have it from Redux
    if (!roomData?.name) return; // Need roomName to fetch

    let cancelled = false;

    const fetchDuration = async () => {
      try {
        const { data } = await getDurationMinutes({
          variables: { userId, roomName: roomData.name },
          fetchPolicy: "network-only",
        });
        if (
          !cancelled &&
          data?.room?.getUserDurationMinutes !== null &&
          data?.room?.getUserDurationMinutes !== undefined
        ) {
          setFetchedDurationMinutes(data.room.getUserDurationMinutes);
        }
      } catch (error) {
        console.error("Failed to fetch duration minutes:", error);
      }
    };

    fetchDuration();

    return () => {
      cancelled = true;
    };
  }, [
    userId,
    isPatient,
    patient?.duration_minutes,
    roomData?.name,
    getDurationMinutes,
  ]);

  useEffect(() => {
    if (!userId || !isPatient) return;

    let cancelled = false;
    let intervalId: NodeJS.Timeout | null = null;

    const init = async () => {
      const now = Date.now();

      const { data } = await getStartTime({
        variables: { userId },
        fetchPolicy: "network-only",
      });

      let start = data?.room.getUserStartTime
        ? Number(data.room.getUserStartTime)
        : null;

      // Logic: Show timer if start_time exists OR if duration_minutes exists
      // Only hide if both are falsy
      // If start_time exists, we can show timer (even if duration is 0, we'll handle it)
      // If start_time doesn't exist, we need duration_minutes to set it

      if (!start) {
        // No start time - need duration_minutes to initialize
        if (!durationSeconds) {
          // Both are falsy - don't show timer
          setRemainingSeconds(null);
          setIsMeetOver(false);
          setShowWarningDialog(false);
          return;
        }
        // Set start time now
        start = now;
        await setStartTime({
          variables: { userId, startTime: String(start) },
        });
      }

      // If we have start but no duration, we can't calculate remaining
      // But user said show timer if start exists. For now, require both for countdown.
      if (!durationSeconds) {
        setRemainingSeconds(null);
        setIsMeetOver(false);
        setShowWarningDialog(false);
        return;
      }

      if (cancelled || !start) return;

      const computeRemaining = () => {
        const elapsed = Math.max(
          0,
          Math.floor((Date.now() - (start as number)) / 1000)
        );
        const remaining = durationSeconds - elapsed;
        return remaining;
      };

      const first = computeRemaining();
      if (first <= 0) {
        setRemainingSeconds(0);
        setIsMeetOver(true);
        return;
      }
      setRemainingSeconds(first);

      intervalId = setInterval(() => {
        if (cancelled) {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        const rem = computeRemaining();
        if (rem <= 0) {
          setRemainingSeconds(0);
          setIsMeetOver(true);
          if (intervalId) clearInterval(intervalId);
          return;
        }
        setRemainingSeconds(rem);
      }, 1000);
    };

    init();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [userId, isPatient, durationSeconds, getStartTime, setStartTime]);

  useEffect(() => {
    if (
      remainingSeconds !== null &&
      remainingSeconds <= 120 &&
      !isMeetOver &&
      durationSeconds !== null &&
      durationSeconds > 120
    ) {
      setShowWarningDialog(true);
    } else {
      setShowWarningDialog(false);
    }
  }, [remainingSeconds, isMeetOver, durationSeconds]);

  const resetForDev = useCallback(async () => {
    if (!import.meta.env.DEV) return;
    if (!userId || !isPatient) return;
    if (!durationSeconds) return;
    const now = Date.now();
    await setStartTime({
      variables: { userId, startTime: String(now) },
    });
    setIsMeetOver(false);
    setRemainingSeconds(durationSeconds);
  }, [isPatient, setStartTime, userId, durationSeconds]);

  const timeColor = useMemo(() => {
    if (remainingSeconds === null || isMeetOver) return "text-primary";
    if (remainingSeconds <= 30) return "text-red-500";
    if (remainingSeconds <= 60) return "text-orange-500";
    return "text-primary";
  }, [remainingSeconds, isMeetOver]);

  const borderColor = useMemo(() => {
    if (remainingSeconds === null || isMeetOver) return "border-primary";
    if (remainingSeconds <= 30) return "border-red-500";
    if (remainingSeconds <= 60) return "border-orange-500";
    return "border-primary";
  }, [remainingSeconds, isMeetOver]);

  const bgColor = useMemo(() => {
    if (remainingSeconds === null || isMeetOver) return "bg-primary/10";
    if (remainingSeconds <= 30) return "bg-red-500/10";
    if (remainingSeconds <= 60) return "bg-orange-500/10";
    return "bg-primary/10";
  }, [remainingSeconds, isMeetOver]);

  const remainingMinutes = remainingSeconds
    ? Math.floor(remainingSeconds / 60)
    : 0;

  return {
    remainingSeconds,
    remainingMinutes,
    isMeetOver,
    showWarningDialog,
    timeColor,
    borderColor,
    bgColor,
    resetForDev,
  };
}
